import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  ArrowRightOnRectangleIcon,
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline'

// Components
import CustomLink from '../common/CustomLink'
import NotificationBell from '../notifications/NotificationBell'

// Hooks & Context
import { useAuth } from '../../contexts/AuthContext'

// Types
import type { FC } from 'react'
import type { UserRole } from '../../types/auth'

const Header: FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  const { user, isAuthenticated, logout, isAdmin, isMember, isGuest, canManageCompanies } = useAuth()
  const userMenuRef = useRef<HTMLDivElement>(null)

  const navigation = [
    { name: 'หน้าแรก', href: '/' },
    { name: 'ค้นหา', href: '/search' },
    { name: 'เกี่ยวกับเรา', href: '/about' },
    { name: 'ติดต่อ', href: '/contact' },
  ]

  // Handle "Add Company" button click based on user role
  const handleAddCompanyClick = (e: React.MouseEvent) => {
    e.preventDefault()

    if (!isAuthenticated) {
      // Not logged in -> go to login
      navigate('/login')
    } else if (isAdmin) {
      // Admin -> go to admin add company page
      navigate('/admin/companies/add')
    } else if (isMember) {
      // Member -> go to member create company page
      navigate('/create-company')
    } else {
      // Fallback to login
      navigate('/login')
    }
  }

  const authNavigation = {
    unauthenticated: [
      { name: 'เข้าสู่ระบบ', href: '/login', icon: ArrowRightOnRectangleIcon },
    ],
    userMenu: [
      { name: 'ข้อมูลส่วนตัว', href: '/profile', icon: UserCircleIcon },
      { name: 'บริษัทของฉัน', href: '/my-companies', icon: BuildingOfficeIcon, role: 'MEMBER' },
      { name: 'คำขอ Claim', href: '/my-claims', icon: BuildingOfficeIcon, role: 'MEMBER' },
      { name: 'แดชบอร์ดแอดมิน', href: '/admin', icon: ShieldCheckIcon, role: 'ADMIN' },
      { name: 'การตั้งค่า', href: '/settings', icon: Cog6ToothIcon },
    ]
  }

  const isActive = (path: string) => location.pathname === path

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen)
  const toggleUserMenu = () => setIsUserMenuOpen(!isUserMenuOpen)

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Helper function to check if user can access menu item
  const canAccessMenuItem = (item: any) => {
    if (!item.role) return true

    switch (item.role) {
      case 'MEMBER':
        return isMember
      case 'ADMIN':
        return isAdmin
      default:
        return true
    }
  }

  // Handle logout
  const handleLogout = () => {
    logout()
    navigate('/') // Redirect to homepage after logout
    setIsUserMenuOpen(false)
    setIsMobileMenuOpen(false)
  }

  // Get user display name with safe fallback
  const getUserDisplayName = () => {
    if (!user) return 'ผู้ใช้'

    // Try display_name first
    if (user.display_name && user.display_name.trim()) {
      return user.display_name.trim()
    }

    // Try full_name
    if (user.full_name && user.full_name.trim()) {
      return user.full_name.trim()
    }

    // Try firstName + lastName
    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()
    if (fullName) {
      return fullName
    }

    // Fallback to email
    if (user.email && user.email.trim()) {
      return user.email.trim()
    }

    // Ultimate fallback
    return 'ผู้ใช้'
  }

  // Get user avatar with safe fallback
  const getUserAvatar = () => {
    if (user?.avatarUrl && user.avatarUrl.trim()) {
      return user.avatarUrl.trim()
    }
    return null
  }

  // Get user avatar initial with safe fallback
  const getUserAvatarInitial = () => {
    const displayName = getUserDisplayName()
    if (!displayName || displayName.length === 0) {
      return 'U'
    }

    // Get first character and ensure it's uppercase
    const firstChar = displayName.charAt(0)
    return firstChar ? firstChar.toUpperCase() : 'U'
  }

  return (
    <header className="bg-white/95 backdrop-blur-lg shadow-sm border-b border-neutral-200/60 sticky top-0 z-40">
      <div className="container-wrapper">
        <div className="flex items-center justify-between h-16 lg:h-18">
          {/* Logo - Enhanced with better typography hierarchy */}
          <div className="flex items-center flex-shrink-0">
            <CustomLink
              to="/"
              className="flex items-center space-x-3 hover:opacity-90 transition-all duration-200 group"
            >
              <div className="w-9 h-9 lg:w-10 lg:h-10 bg-gradient-to-br from-primary-600 to-primary-700 rounded-xl flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-200">
                <span className="text-white font-bold text-lg lg:text-xl">T</span>
              </div>
              <div className="flex flex-col">
                <span className="font-bold text-lg lg:text-xl text-neutral-900 leading-tight tracking-tight">
                  ไทยเซ็กคิวริตี้
                </span>
                <span className="text-xs lg:text-sm text-neutral-500 leading-none font-medium">
                  ไดเรกทอรี่
                </span>
              </div>
            </CustomLink>
          </div>

          {/* Desktop Navigation - Enhanced with better visual hierarchy */}
          <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
            {navigation.map((item) => (
              <CustomLink
                key={item.name}
                to={item.href}
                className={`relative px-3 py-2 text-sm lg:text-base font-medium transition-all duration-200 rounded-lg group ${
                  isActive(item.href)
                    ? 'text-primary-700 bg-primary-50 shadow-sm'
                    : 'text-neutral-600 hover:text-primary-600 hover:bg-neutral-50'
                }`}
              >
                <span className="relative z-10">{item.name}</span>
                {/* Active indicator */}
                {isActive(item.href) && (
                  <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-primary-600 rounded-full" />
                )}
                {/* Hover indicator */}
                <div className="absolute inset-0 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
              </CustomLink>
            ))}
          </nav>

          {/* Actions - User Authentication Menu */}
          <div className="hidden lg:flex items-center space-x-3 md:space-x-4 lg:space-x-6">
            {/* Add Company Button - Always Visible */}
            <button
              onClick={handleAddCompanyClick}
              className="inline-flex items-center space-x-2 px-3 lg:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 border bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 border-primary-600 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <PlusCircleIcon className="w-4 h-4" />
              <span className="hidden lg:inline">เพิ่มบริษัทใหม่</span>
              <span className="lg:hidden">เพิ่มบริษัท</span>
            </button>

            {/* Notification Bell - Show only when authenticated */}
            {isAuthenticated && (
              <NotificationBell />
            )}

            {/* Desktop Auth Menu */}
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={toggleUserMenu}
                  className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium transition-all duration-200 rounded-lg border ${
                    isUserMenuOpen
                      ? 'text-primary-700 bg-primary-50 border-primary-200 shadow-sm'
                      : 'text-neutral-600 hover:text-primary-600 border-transparent hover:bg-neutral-50 hover:border-neutral-200'
                  }`}
                >
                  {getUserAvatar() ? (
                    <img
                      src={getUserAvatar()!}
                      alt={getUserDisplayName()}
                      className="w-7 h-7 rounded-full object-cover ring-2 ring-white shadow-sm"
                    />
                  ) : (
                    <div className="w-7 h-7 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center shadow-sm">
                      <span className="text-white text-xs font-semibold">
                        {getUserAvatarInitial()}
                      </span>
                    </div>
                  )}
                  <span className="max-w-32 truncate font-medium">{getUserDisplayName()}</span>
                  <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${
                    isUserMenuOpen ? 'rotate-180' : ''
                  }`} />
                </button>

                {/* User Dropdown Menu - Enhanced with better visual hierarchy */}
                <AnimatePresence>
                  {isUserMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -10, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-xl border border-neutral-200/60 py-2 z-50 backdrop-blur-sm"
                    >
                      {authNavigation.userMenu
                        .filter(canAccessMenuItem)
                        .map((item) => {
                          const Icon = item.icon
                          return (
                            <CustomLink
                              key={item.name}
                              to={item.href}
                              onClick={() => setIsUserMenuOpen(false)}
                              className="flex items-center space-x-3 px-4 py-2 text-sm text-neutral-700 hover:text-primary-600 hover:bg-neutral-50 transition-colors duration-200"
                            >
                              <Icon className="w-4 h-4" />
                              <span>{item.name}</span>
                            </CustomLink>
                          )
                        })}

                      <hr className="my-2 border-neutral-200" />

                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 transition-colors duration-200 w-full text-left"
                      >
                        <ArrowRightOnRectangleIcon className="w-4 h-4" />
                        <span>ออกจากระบบ</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <div className="flex items-center space-x-2 lg:space-x-3">
                {authNavigation.unauthenticated.map((item) => {
                  const Icon = item.icon
                  return (
                    <CustomLink
                      key={item.name}
                      to={item.href}
                      className="inline-flex items-center space-x-2 px-3 lg:px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 border text-neutral-600 hover:text-primary-600 border-transparent hover:bg-neutral-50 hover:border-neutral-200"
                    >
                      <Icon className="w-4 h-4" />
                      <span>{item.name}</span>
                    </CustomLink>
                  )
                })}
              </div>
            )}
          </div>

          {/* Mobile Actions - Enhanced with better visual feedback */}
          <div className="flex items-center space-x-1 md:hidden">
            <button
              onClick={toggleMobileMenu}
              className={`p-2.5 rounded-lg transition-all duration-200 ${
                isMobileMenuOpen
                  ? 'text-primary-600 bg-primary-50'
                  : 'text-neutral-600 hover:text-primary-600 hover:bg-neutral-50'
              }`}
              aria-label="เปิดเมนู"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="w-5 h-5" />
              ) : (
                <Bars3Icon className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu - Enhanced with better visual hierarchy */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="md:hidden border-t border-neutral-200 bg-gradient-to-b from-white to-neutral-50"
            >
              <nav className="py-6 space-y-1">
                {/* Main Navigation - Enhanced with better active states */}
                {navigation.map((item) => (
                  <CustomLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`block mx-4 px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 relative ${
                      isActive(item.href)
                        ? 'text-primary-700 bg-gradient-to-r from-primary-50 to-primary-100 shadow-sm border border-primary-200'
                        : 'text-neutral-700 hover:text-primary-600 hover:bg-white hover:shadow-sm'
                    }`}
                  >
                    <span className="flex items-center justify-between">
                      {item.name}
                      {isActive(item.href) && (
                        <div className="w-2 h-2 bg-primary-600 rounded-full" />
                      )}
                    </span>
                  </CustomLink>
                ))}

                {/* Mobile Auth Menu - Enhanced spacing */}
                <div className="pt-6 mt-4 border-t border-neutral-200">
                  {/* Add Company Button for Mobile - Always Visible */}
                  <button
                    onClick={(e) => {
                      handleAddCompanyClick(e)
                      setIsMobileMenuOpen(false)
                    }}
                    className="flex items-center space-x-4 mx-4 px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:from-primary-700 hover:to-primary-800 shadow-md mb-3"
                  >
                    <PlusCircleIcon className="w-5 h-5" />
                    <span>เพิ่มบริษัทใหม่</span>
                  </button>

                  {isAuthenticated ? (
                    <div className="space-y-2">
                      {/* User Info - Enhanced with better visual hierarchy */}
                      <div className="mx-4 p-4 bg-gradient-to-r from-neutral-50 to-neutral-100 rounded-xl border border-neutral-200">
                        <div className="flex items-center space-x-4">
                          {getUserAvatar() ? (
                            <img
                              src={getUserAvatar()!}
                              alt={getUserDisplayName()}
                              className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-700 rounded-full flex items-center justify-center shadow-sm">
                              <span className="text-white text-sm font-bold">
                                {getUserAvatarInitial()}
                              </span>
                            </div>
                          )}
                          <div className="flex-1">
                            <p className="text-base font-semibold text-neutral-900">
                              {getUserDisplayName()}
                            </p>
                            <p className="text-sm text-neutral-600 font-medium">
                              {user?.role === 'SUPER_ADMIN' || user?.role === 'ADMIN' ? 'ผู้ดูแลระบบ' :
                               user?.role === 'COMPANY_OWNER' || user?.role === 'MEMBER' || user?.role === 'USER' ? 'สมาชิก' : 'สมาชิก'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* User Menu Items - Enhanced with better spacing */}
                      {authNavigation.userMenu
                        .filter(canAccessMenuItem)
                        .map((item) => {
                          const Icon = item.icon
                          return (
                            <CustomLink
                              key={item.name}
                              to={item.href}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className="flex items-center space-x-4 mx-4 px-4 py-3 text-base font-medium text-neutral-700 hover:text-primary-600 hover:bg-white hover:shadow-sm rounded-xl transition-all duration-200"
                            >
                              <Icon className="w-5 h-5" />
                              <span>{item.name}</span>
                            </CustomLink>
                          )
                        })}

                      {/* Logout Button - Enhanced with better visual emphasis */}
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-4 mx-4 px-4 py-3 text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-xl transition-all duration-200 w-auto border border-red-200 hover:border-red-300"
                      >
                        <ArrowRightOnRectangleIcon className="w-5 h-5" />
                        <span>ออกจากระบบ</span>
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {authNavigation.unauthenticated.map((item) => {
                        const Icon = item.icon
                        return (
                          <CustomLink
                            key={item.name}
                            to={item.href}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className="flex items-center space-x-4 mx-4 px-4 py-3 text-base font-medium rounded-xl transition-all duration-200 text-neutral-700 hover:text-primary-600 hover:bg-white hover:shadow-sm border border-neutral-200 hover:border-primary-200"
                          >
                            <Icon className="w-5 h-5" />
                            <span>{item.name}</span>
                          </CustomLink>
                        )
                      })}
                    </div>
                  )}
                </div>
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </header>
  )
}

export default Header